import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { truckingBillAPI } from '../../api/waybillAPI';

const PHP = (n) => new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2 }).format(n || 0);
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-PH') : '—');

const customerNameOf = (bill) => (
  bill.WayBillId?.CustomerId?.CustomerName
  || bill.WayBillId?.CustomerId?.name
  || '—'
);

const daysOverdue = (b) => {
  if (b.status !== 'overdue') return null;
  const diff = Math.floor((Date.now() - new Date(b.BillDueDate)) / 86400000);
  return diff > 0 ? diff : null;
};

function aging(bills) {
  const buckets = { current: [], d30: [], d60: [], d90: [], d90plus: [] };
  bills.forEach((b) => {
    if (b.status !== 'overdue') {
      buckets.current.push(b);
      return;
    }
    const days = Math.floor((Date.now() - new Date(b.BillDueDate)) / 86400000);
    if (days <= 30) buckets.d30.push(b);
    else if (days <= 60) buckets.d60.push(b);
    else if (days <= 90) buckets.d90.push(b);
    else buckets.d90plus.push(b);
  });
  return buckets;
}

const bucketTotal = (arr) => arr.reduce((sum, b) => sum + (b.amountDue || 0), 0);

const fmtLong = (dateStr) => {
  if (!dateStr) return 'All Dates';
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-PH', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export default function ARPrintPage() {
  const [searchParams] = useSearchParams();
  const dateFrom = searchParams.get('dateFrom') || '';
  const dateTo = searchParams.get('dateTo') || '';
  const status = searchParams.get('status') || '';
  const customer = searchParams.get('customer') || '';

  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    truckingBillAPI.getARSummary({ dateFrom, dateTo, status })
      .then((res) => {
        const source = Array.isArray(res.data?.data?.bills) ? res.data.data.bills : [];
        const filtered = customer
          ? source.filter((b) => customerNameOf(b).toLowerCase().includes(customer.toLowerCase()))
          : source;
        setBills(filtered);
        setLoading(false);
        setTimeout(() => window.print(), 400);
      })
      .catch(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dateLabel = useMemo(() => {
    if (!dateFrom && !dateTo) return 'All Dates';
    if (dateFrom && dateTo && dateFrom === dateTo) return fmtLong(dateFrom);
    return `${fmtLong(dateFrom)} - ${fmtLong(dateTo)}`;
  }, [dateFrom, dateTo]);

  useEffect(() => {
    document.title = `AR Report - ${dateLabel}`;
  }, [dateLabel]);

  const summary = useMemo(() => ({
    totalAR: bills.reduce((sum, b) => sum + (b.amountDue || 0), 0),
    overdueAR: bills.filter((b) => b.status === 'overdue').reduce((sum, b) => sum + (b.amountDue || 0), 0),
    currentAR: bills.filter((b) => b.status !== 'overdue').reduce((sum, b) => sum + (b.amountDue || 0), 0),
    count: bills.length,
  }), [bills]);

  const buckets = useMemo(() => aging(bills), [bills]);

  if (loading) return <p style={{ padding: 40 }}>Loading report...</p>;

  return (
    <>
      <style>{`
        body { font-family: Arial, sans-serif; font-size: 10pt; margin: 0; }
        @page { size: A4 landscape; margin: 12mm; }
        @media print { .no-print { display: none !important; } }
        .header { text-align: center; margin-bottom: 8px; }
        h1 { font-size: 14pt; margin: 0; }
        h2 { font-size: 11pt; margin: 2px 0; font-weight: normal; }
        .meta { font-size: 9pt; color: #555; margin-bottom: 8px; }
        .summary { margin-bottom: 10px; font-size: 9.5pt; }
        .summary span { margin-right: 20px; }
        table { width: 100%; border-collapse: collapse; font-size: 9pt; }
        th { background: #f0f0f0; border: 1px solid #ddd; padding: 4px 8px; text-align: left; font-size: 8.8pt; }
        td { border: 1px solid #ddd; padding: 4px 8px; }
        tr:nth-child(even) { background: #fafafa; }
      `}</style>

      <div className="header">
        <h1>Accounts Receivable Report</h1>
        <h2>{dateLabel}</h2>
        <div className="meta">
          {status ? <span style={{ marginRight: 16 }}>Status: {status}</span> : null}
          {customer ? <span>Customer: {customer}</span> : null}
        </div>
      </div>

      <div className="summary">
        <span>Total AR: <strong>PHP {PHP(summary.totalAR)}</strong></span>
        <span>Overdue: <strong>PHP {PHP(summary.overdueAR)}</strong></span>
        <span>Current: <strong>PHP {PHP(summary.currentAR)}</strong></span>
        <span>Invoices: <strong>{summary.count}</strong></span>
      </div>

      <table>
        <thead>
          <tr>
            <th>Bill No.</th>
            <th>Waybill Serial</th>
            <th>Customer</th>
            <th>Bill Date</th>
            <th>Due Date</th>
            <th>Amount Due</th>
            <th>Days Overdue</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {bills.length === 0 ? (
            <tr>
              <td colSpan={8} style={{ textAlign: 'center', padding: 16 }}>No AR records found.</td>
            </tr>
          ) : bills.map((b) => {
            const overdueDays = daysOverdue(b);
            return (
              <tr key={b._id}>
                <td>{b.BillName || '—'}</td>
                <td>{b.WayBillId?.SerialNo || '—'}</td>
                <td>{customerNameOf(b)}</td>
                <td>{fmt(b.BillDate)}</td>
                <td>{fmt(b.BillDueDate)}</td>
                <td style={{ textAlign: 'right' }}>PHP {PHP(b.amountDue)}</td>
                <td style={{ textAlign: 'center' }}>{overdueDays || '—'}</td>
                <td>{b.status || 'draft'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ marginTop: 14 }}>
        <h3 style={{ fontSize: '10pt', margin: '0 0 6px' }}>Overdue Aging Summary</h3>
        <table>
          <thead>
            <tr>
              <th>Bucket</th>
              <th style={{ textAlign: 'right' }}>Count</th>
              <th style={{ textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {[
              { label: 'Current (not yet due)', key: 'current' },
              { label: '1-30 days overdue', key: 'd30' },
              { label: '31-60 days overdue', key: 'd60' },
              { label: '61-90 days overdue', key: 'd90' },
              { label: '90+ days overdue', key: 'd90plus' },
            ].map(({ label, key }) => (
              <tr key={key}>
                <td>{label}</td>
                <td style={{ textAlign: 'right' }}>{buckets[key].length}</td>
                <td style={{ textAlign: 'right' }}>PHP {PHP(bucketTotal(buckets[key]))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="no-print" style={{ marginTop: 16 }}>
        <button onClick={() => window.print()} style={{ padding: '6px 16px' }}>Print</button>
      </div>
    </>
  );
}
